import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogIn, LogOut, Clock, Calendar, Users } from 'lucide-react';

export default function AttendancePage() {
  const { user } = useAuth();
  const [todayRecord, setTodayRecord] = useState(null);
  const [records, setRecords] = useState([]);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkNote, setCheckNote] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const isAdmin = ['super_admin', 'admin', 'manager'].includes(user?.role);

  const fetchData = async () => {
    try {
      const [todayRes, recordsRes] = await Promise.all([
        api.get('/attendance/today'),
        api.get('/attendance', { params: { ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }) } })
      ]);
      setTodayRecord(todayRes.data);
      setRecords(recordsRes.data);
      if (isAdmin) {
        const reportRes = await api.get('/attendance/report', { params: { ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }) } });
        setReport(reportRes.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [dateFrom, dateTo]);

  const handleCheckIn = async () => {
    try {
      await api.post('/attendance/check-in', { notes: checkNote });
      toast.success('Checked in successfully');
      setCheckNote('');
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleCheckOut = async () => {
    try {
      const { data } = await api.post('/attendance/check-out', { notes: checkNote });
      toast.success(`Checked out. Working hours: ${data.working_hours}h`);
      setCheckNote('');
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const isCheckedIn = todayRecord?.status === 'checked_in';
  const isCheckedOut = todayRecord?.status === 'checked_out';

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Attendance</h1>
        <p className="text-sm text-brand-text-secondary mt-1">Daily check-in / check-out tracking</p>
      </div>

      {/* Check-in/out Card */}
      <Card className="border border-brand-border bg-white">
        <CardHeader><CardTitle className="font-heading text-lg text-brand-text-primary flex items-center gap-2"><Clock size={20} /> Today's Status</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            {isCheckedOut ? (
              <Badge className="text-sm bg-brand-success/15 text-brand-success border-0 px-4 py-1">Checked Out - {todayRecord?.working_hours}h worked</Badge>
            ) : isCheckedIn ? (
              <Badge className="text-sm bg-amber-100 text-amber-700 border-0 px-4 py-1">Checked In at {new Date(todayRecord.check_in_time).toLocaleTimeString()}</Badge>
            ) : (
              <Badge className="text-sm bg-gray-100 text-gray-600 border-0 px-4 py-1">Not checked in yet</Badge>
            )}
          </div>
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[200px] space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={checkNote} onChange={e => setCheckNote(e.target.value)} placeholder="Add a note..." className="bg-white border-brand-border rounded-sm h-10" data-testid="attendance-notes" />
            </div>
            {!isCheckedIn && !isCheckedOut && (
              <Button onClick={handleCheckIn} className="bg-brand-success hover:bg-brand-success/90 text-white rounded-sm" data-testid="check-in-btn">
                <LogIn size={16} className="mr-2" /> Check In
              </Button>
            )}
            {isCheckedIn && (
              <Button onClick={handleCheckOut} className="bg-brand-secondary hover:bg-brand-secondary-hover text-white rounded-sm" data-testid="check-out-btn">
                <LogOut size={16} className="mr-2" /> Check Out
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap items-end">
        <div className="space-y-2">
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-white border-brand-border rounded-sm w-44" data-testid="attendance-date-from" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-white border-brand-border rounded-sm w-44" data-testid="attendance-date-to" />
        </div>
      </div>

      {/* Attendance Report (Admin) */}
      {isAdmin && report.length > 0 && (
        <Card className="border border-brand-border bg-white">
          <CardHeader><CardTitle className="font-heading text-lg text-brand-text-primary flex items-center gap-2"><Users size={20} /> Team Summary</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="attendance-report-table">
                <thead>
                  <tr className="border-b border-brand-border bg-brand-bg/50">
                    {['Trader', 'Total Days', 'Total Hours', 'Avg Hours/Day', 'Active'].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.map((r, i) => (
                    <tr key={r.user_id || r.user_name || i} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                      <td className="py-3 px-4 font-medium text-brand-text-primary">{r.user_name}</td>
                      <td className="py-3 px-4 text-brand-text-secondary">{r.total_days}</td>
                      <td className="py-3 px-4 text-brand-text-primary font-medium">{r.total_hours}h</td>
                      <td className="py-3 px-4 text-brand-text-secondary">{r.avg_hours}h</td>
                      <td className="py-3 px-4">
                        <Badge className={`text-xs border-0 ${r.checked_in_count > 0 ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>
                          {r.checked_in_count > 0 ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attendance Records */}
      <Card className="border border-brand-border bg-white">
        <CardHeader><CardTitle className="font-heading text-lg text-brand-text-primary flex items-center gap-2"><Calendar size={20} /> Records</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attendance-records-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Date', ...(isAdmin ? ['Name'] : []), 'Check In', 'Check Out', 'Hours', 'Status', 'Notes'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 records.length === 0 ? <tr><td colSpan={7} className="py-12 text-center text-brand-text-secondary">No records found</td></tr> :
                 records.map(r => (
                  <tr key={r.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 text-brand-text-primary">{r.date}</td>
                    {isAdmin && <td className="py-3 px-4 text-brand-text-primary">{r.user_name}</td>}
                    <td className="py-3 px-4 text-brand-text-secondary">{r.check_in_time ? new Date(r.check_in_time).toLocaleTimeString() : '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString() : '-'}</td>
                    <td className="py-3 px-4 font-medium text-brand-text-primary">{r.working_hours ? `${r.working_hours}h` : '-'}</td>
                    <td className="py-3 px-4">
                      <Badge className={`text-xs border-0 ${r.status === 'checked_out' ? 'bg-brand-success/15 text-brand-success' : r.status === 'checked_in' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.status?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-brand-text-secondary max-w-[200px] truncate">{r.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
